/* MĚŘICÍ KÓDY NA VEŘEJNÝCH MARKETINGOVÝCH STRÁNKÁCH PORADCE (Meta Pixel, GA4, GTM).
 *
 * PRAVIDLA (GDPR + ePrivacy):
 *   • skript se vloží AŽ PO SOUHLASU návštěvníka (lišta v components/web/MericiKody.js),
 *   • jen na marketingových stránkách poradce: /w/:slug, /l/:tema/:slug, /t/:id/:slug,
 *     /kviz/:slug — nikdy v CRM, nikdy na klientských formulářích /f/:token,
 *   • souhlas se pamatuje v localStorage ZVLÁŠŤ pro každého poradce (slug),
 *     protože měří on, ne appka,
 *   • událost Lead (Meta) / generate_lead (GA4) se pošle jen po ÚSPĚŠNÉM odeslání
 *     formuláře a jen když skript běží (= byl souhlas).
 *
 * Formáty ID hlídá totéž pravidlo jako backend (lib/merici_kody.py) — matice
 * příkladů je v obou testech stejná, aby se obě strany nerozešly.
 */

export const RE_META_PIXEL = /^\d{15,16}$/;
export const RE_GA4 = /^G-[A-Z0-9]{4,12}$/;
export const RE_GTM = /^GTM-[A-Z0-9]{4,10}$/;

const ocisti = (v) => String(v ?? "").trim().toUpperCase();

export const platnyMetaPixel = (v) => RE_META_PIXEL.test(String(v ?? "").trim());
export const platnyGa4 = (v) => RE_GA4.test(ocisti(v));
export const platnyGtm = (v) => RE_GTM.test(ocisti(v));

/** Normalizace pro uložení: prázdné = "", jinak trim (+ velká písmena u G-/GTM-). */
export function normalizujKody(f) {
  return {
    web_meta_pixel_id: String(f?.web_meta_pixel_id ?? "").trim(),
    web_ga4_id: ocisti(f?.web_ga4_id),
    web_gtm_id: ocisti(f?.web_gtm_id),
  };
}

/** Chyby formátu → { pole: klicZpravy } (prázdné = v pořádku). Prázdná hodnota je vždy OK. */
export function chybyKodu(f) {
  const n = normalizujKody(f);
  const out = {};
  if (n.web_meta_pixel_id && !platnyMetaPixel(n.web_meta_pixel_id)) out.web_meta_pixel_id = "meta";
  if (n.web_ga4_id && !platnyGa4(n.web_ga4_id)) out.web_ga4_id = "ga4";
  if (n.web_gtm_id && !platnyGtm(n.web_gtm_id)) out.web_gtm_id = "gtm";
  return out;
}

/* Veřejné marketingové stránky poradce — jen tady smí být lišta i skripty. */
export const RE_MARKETINGOVA_STRANKA = /^\/(w|l|t|kviz)\/[^/]+/;
export const naMarketingoveStrance = (pathname) => RE_MARKETINGOVA_STRANKA.test(String(pathname || ""));

/* ── Souhlas ──────────────────────────────────────────────────────────── */
export const klicSouhlasu = (slug) => `bp_mereni_souhlas_v1:${String(slug || "").toLowerCase()}`;

export function nactiSouhlas(slug) {
  try {
    const raw = window.localStorage.getItem(klicSouhlasu(slug));
    if (!raw) return null;
    const v = JSON.parse(raw);
    return v && typeof v.mereni === "boolean" ? v : null;
  } catch { return null; }
}

export function ulozSouhlas(slug, mereni) {
  try { window.localStorage.setItem(klicSouhlasu(slug), JSON.stringify({ mereni: !!mereni, ts: Date.now() })); } catch { /* privátní režim */ }
}

/* ── Vložení skriptů (idempotentní; volat JEN po souhlasu) ────────────── */
const ATTR = "data-bp-mereni";

function pridejSkript(doc, id, { src, text }) {
  if (doc.querySelector(`script[${ATTR}="${id}"]`)) return false;
  const s = doc.createElement("script");
  s.setAttribute(ATTR, id);
  if (src) { s.async = true; s.src = src; }
  if (text) s.text = text;
  doc.head.appendChild(s);
  return true;
}

/**
 * Vloží měřicí kódy podle ID. Nevalidní ID ignoruje (obrana proti injekci
 * z cizího profilu). Vrací seznam toho, co se vložilo.
 */
export function vlozMericiKody(kody, doc = typeof document !== "undefined" ? document : null, w = typeof window !== "undefined" ? window : null) {
  if (!doc || !w) return [];
  const vlozeno = [];
  const meta = String(kody?.meta_pixel_id || "").trim();
  const ga4 = ocisti(kody?.ga4_id);
  const gtm = ocisti(kody?.gtm_id);

  if (gtm && platnyGtm(gtm)) {
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
    if (pridejSkript(doc, "gtm", { src: `https://www.googletagmanager.com/gtm.js?id=${gtm}` })) vlozeno.push("gtm");
  }
  if (ga4 && platnyGa4(ga4)) {
    w.dataLayer = w.dataLayer || [];
    if (typeof w.gtag !== "function") {
      w.gtag = function gtag() { w.dataLayer.push(arguments); }; // eslint-disable-line prefer-rest-params
      w.gtag("js", new Date());
    }
    w.gtag("config", ga4, { anonymize_ip: true });
    if (pridejSkript(doc, "ga4", { src: `https://www.googletagmanager.com/gtag/js?id=${ga4}` })) vlozeno.push("ga4");
  }
  if (meta && platnyMetaPixel(meta)) {
    if (typeof w.fbq !== "function") {
      /* Oficiální zavaděč Meta Pixelu (fronta, než doběhne fbevents.js). */
      const n = function fbq() { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); }; // eslint-disable-line prefer-rest-params
      n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
      w.fbq = n; if (!w._fbq) w._fbq = n;
    }
    w.fbq("init", meta);
    w.fbq("track", "PageView");
    if (pridejSkript(doc, "meta", { src: "https://connect.facebook.net/en_US/fbevents.js" })) vlozeno.push("meta");
  }
  return vlozeno;
}

/** Po úspěšném odeslání leadu. Bez souhlasu skript neběží → nic se nepošle. */
export function udalostLead(w = typeof window !== "undefined" ? window : null) {
  if (!w) return { meta: false, ga4: false };
  const out = { meta: false, ga4: false };
  try { if (typeof w.fbq === "function") { w.fbq("track", "Lead"); out.meta = true; } } catch { /* cizí skript */ }
  try { if (typeof w.gtag === "function") { w.gtag("event", "generate_lead"); out.ga4 = true; } } catch { /* cizí skript */ }
  return out;
}

export const maNejakyKod = (k) => !!(k && (platnyMetaPixel(k.meta_pixel_id) || platnyGa4(k.ga4_id) || platnyGtm(k.gtm_id)));
